import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, X, Ruler, Share2 } from "lucide-react";
import { formatPrice, resolveImg, mainImage } from "@/lib/format";

const ProductCard = ({ product, index, onOpen }) => {
  const imgs = (product.images || []).map(resolveImg).filter(Boolean);
  const primary = imgs[0] || mainImage(product);
  const secondary = imgs[1];
  return (
    <motion.button
      data-testid={`product-card-${product.id}`}
      onClick={() => onOpen(product)}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: (index % 3) * 0.08 }}
      className="group text-left"
    >
      <div className="relative overflow-hidden aspect-[3/4] bg-aak-muted">
        <img
          src={primary}
          alt={product.name}
          className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] ${
            secondary ? "group-hover:opacity-0" : "group-hover:scale-105"
          }`}
        />
        {secondary && (
          <img
            src={secondary}
            alt={`${product.name} detail`}
            className="absolute inset-0 w-full h-full object-cover object-top opacity-0 scale-105 group-hover:opacity-100 group-hover:scale-100 transition-all duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)]"
          />
        )}
        <div className="absolute bottom-4 right-4 w-11 h-11 bg-aak-bg text-aak-fg flex items-center justify-center translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 z-10">
          <Plus className="w-5 h-5" strokeWidth={1.5} />
        </div>
      </div>
      <div className="mt-5 flex items-baseline justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.22em] text-aak-primary mb-1">{product.category}</p>
          <h3 className="font-serif text-2xl text-aak-fg leading-tight">{product.name}</h3>
        </div>
        <span className="font-sans text-sm text-aak-mutedfg whitespace-nowrap">{formatPrice(product.price)}</span>
      </div>
    </motion.button>
  );
};

export const Shop = () => {
  const [products, setProducts] = useState([]);
  const [active, setActive] = useState(null);
  const [size, setSize] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [chart, setChart] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [filter, setFilter] = useState("All");
  const { addItem } = useCart();
  const { id: routeId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/size-chart").then((r) => setChart(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (routeId && products.length) {
      const p = products.find((x) => x.id === routeId);
      if (p) {
        setActive(p);
        setSize(null);
        setActiveImg(0);
        setShowChart(false);
      } else {
        setActive(null);
      }
    } else if (!routeId) {
      setActive(null);
    }
  }, [routeId, products]);

  const openProduct = (p) => navigate(`/product/${p.id}`);
  const closeProduct = () => navigate("/");

  const shareProduct = async (p) => {
    const url = `${window.location.origin}/product/${p.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: p.name, text: `${p.name} — House of AAK`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch (e) {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      } catch {
        toast.error("Could not copy link");
      }
    }
  };

  const handleAdd = () => {
    if (!size) {
      toast.error("Please select a size");
      return;
    }
    addItem(active, size);
    toast.success(`${active.name} added to your bag`);
    closeProduct();
  };

  const activeImgs = active ? (active.images || []).map(resolveImg).filter(Boolean) : [];

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const filtered = filter === "All" ? products : products.filter((p) => p.category === filter);

  return (
    <section id="shop" className="py-24 md:py-32 px-6 md:px-12 max-w-[1600px] mx-auto scroll-mt-24">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 md:mb-24 gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-aak-primary mb-4">The Collection</p>
          <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tighter leading-none text-aak-fg">
            Heirlooms for<br />
            <span className="italic text-aak-primary">every</span> celebration
          </h2>
        </div>
        <p className="max-w-xs text-sm text-aak-mutedfg leading-relaxed">
          Hand-finished salwar suits, co-ord sets and dresses — made in small
          runs, cut in earth tones and lace.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-12 md:mb-16" data-testid="category-filters">
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => setFilter(c)}
            className={`relative px-5 py-2.5 text-[11px] uppercase tracking-[0.2em] border transition-colors duration-400 ${
              filter === c
                ? "bg-aak-fg text-aak-bg border-aak-fg"
                : "border-aak-border text-aak-mutedfg hover:border-aak-fg hover:text-aak-fg"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
        {filtered.map((p, i) => (
          <ProductCard key={p.id} product={p} index={i} onOpen={openProduct} />
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && closeProduct()}>
        <DialogContent
          className="max-w-4xl p-0 gap-0 bg-aak-bg border border-aak-border rounded-none overflow-hidden max-h-[92vh] overflow-y-auto [&>button]:hidden"
          data-testid="product-dialog"
        >
          {active && (
            <div className="grid grid-cols-1 md:grid-cols-2">
              <DialogTitle className="sr-only">{active.name}</DialogTitle>
              <div className="bg-aak-muted">
                <div className="relative aspect-[3/4]">
                  <img
                    src={activeImgs[activeImg] || mainImage(active)}
                    alt={active.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {activeImgs.length > 1 && (
                  <div className="flex gap-2 p-3">
                    {activeImgs.map((im, i) => (
                      <button
                        key={i}
                        data-testid={`thumb-${i}`}
                        onClick={() => setActiveImg(i)}
                        className={`w-16 h-20 overflow-hidden border ${
                          activeImg === i ? "border-aak-fg" : "border-transparent opacity-70"
                        }`}
                      >
                        <img src={im} alt="" className="w-full h-full object-cover object-top" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-8 md:p-10 flex flex-col">
                <div className="self-end flex items-center gap-3">
                  <button
                    data-testid="share-product-btn"
                    onClick={() => shareProduct(active)}
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-aak-primary hover:text-aak-fg transition-colors"
                  >
                    <Share2 className="w-4 h-4" strokeWidth={1.5} /> Share
                  </button>
                  <button
                    data-testid="dialog-close-btn"
                    onClick={closeProduct}
                    className="text-aak-mutedfg hover:text-aak-fg transition-colors"
                  >
                    <X className="w-5 h-5" strokeWidth={1.5} />
                  </button>
                </div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-aak-primary mb-3">{active.category}</p>
                <h3 className="font-serif text-4xl md:text-5xl leading-none text-aak-fg mb-4">{active.name}</h3>
                <p className="font-sans text-lg text-aak-fg mb-6">{formatPrice(active.price)}</p>
                <p className="text-sm text-aak-mutedfg leading-relaxed mb-2">{active.description}</p>
                {active.material && (
                  <p className="text-xs uppercase tracking-[0.15em] text-aak-mutedfg mb-8">{active.material}</p>
                )}

                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-aak-fg">Select Size</p>
                  {chart && (
                    <button
                      data-testid="size-guide-btn"
                      onClick={() => setShowChart(true)}
                      className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-aak-primary link-underline"
                    >
                      <Ruler className="w-3.5 h-3.5" strokeWidth={1.5} /> Size Guide
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                  {(active.sizes || ["S", "M", "L", "XL"]).map((s) => (
                    <button
                      key={s}
                      data-testid={`size-${s}`}
                      onClick={() => setSize(s)}
                      className={`w-11 h-11 text-xs border transition-colors duration-300 ${
                        size === s
                          ? "bg-aak-fg text-aak-bg border-aak-fg"
                          : "border-aak-border text-aak-fg hover:border-aak-fg"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>

                <button
                  data-testid="add-to-cart-btn"
                  onClick={handleAdd}
                  className="mt-auto w-full bg-aak-fg text-aak-bg py-4 text-xs uppercase tracking-[0.25em] hover:bg-aak-primary transition-colors duration-500"
                >
                  Add to Bag
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showChart} onOpenChange={setShowChart}>
        <DialogContent
          className="max-w-2xl p-0 gap-0 bg-aak-bg border border-aak-border rounded-none overflow-hidden [&>button]:hidden"
          data-testid="size-chart-dialog"
        >
          {chart && (
            <div>
              <div className="flex items-center justify-between px-6 py-4 border-b border-aak-border">
                <h4 className="font-serif text-2xl text-aak-fg">Size Guide</h4>
                <DialogTitle className="sr-only">Size Guide</DialogTitle>
                <button
                  data-testid="size-chart-close"
                  onClick={() => setShowChart(false)}
                  className="text-aak-mutedfg hover:text-aak-fg"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <img src={resolveImg(chart.image)} alt="House of AAK size chart" className="w-full h-auto" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
